import {
  STRATEGY_TAG_TYPE,
  ALL_STRATEGIES_ID,
  USER_STRATEGIES_ID,
  MY_CHAINED_STRATEGIES_ID,
} from './constants';
import { strategyApi } from './strategyApi';
import { buildSearchParamsAuto } from '../utils';

interface MyChainedStrategiesParams {
  q?: string;
  [key: string]: any;
}

export const chainedStrategiesApi = strategyApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllChainedStrategies: builder.query<
      MedScout.PaginatedResponse & { results: MedScout.ChainedStrategy[] },
      MyChainedStrategiesParams | void
    >({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        const queryString = queryParams?.toString();
        return {
          url: `v1/strategies/strategy-chains/?${queryString}`,
        };
      },
      providesTags: (result) => {
        const strategies = Array.isArray(result) ? result : [];
        return [
          {
            type: STRATEGY_TAG_TYPE,
            id: ALL_STRATEGIES_ID,
          },
          ...strategies.map(({ id }: any) => ({
            type: STRATEGY_TAG_TYPE,
            id,
          })),
        ];
      },
    }),
    getProviderStrategyTabs: builder.query<
      { results: MedScout.ChainedStrategy[]; count: number },
      {
        providerId: string;
        page?: number;
        pageSize?: number;
        q?: string;
        sort?: string;
        order?: string;
      }
    >({
      query: ({ providerId, ...queryParams }) => {
        const searchParams = buildSearchParamsAuto(queryParams);
        return {
          url: `v1/strategies/strategy-chains/providers/${providerId}/strategy-tabs/?${searchParams.toString()}`,
        };
      },
      providesTags: (result, error, { providerId }) => [
        { type: STRATEGY_TAG_TYPE, id: `provider-${providerId}-tabs` },
      ],
    }),
    createChainedStrategy: builder.mutation<MedScout.ChainedStrategy, Record<string, any>>({
      query: (chainedStrategy) => ({
        url: 'v1/strategies/strategy-chains/',
        method: 'POST',
        body: chainedStrategy,
      }),
      invalidatesTags: [
        { type: STRATEGY_TAG_TYPE, id: ALL_STRATEGIES_ID },
        { type: STRATEGY_TAG_TYPE, id: MY_CHAINED_STRATEGIES_ID },
      ],
    }),
    // TODO: Remove once BE includes target_strategy/engage_strategies in list response
    getAllChainedStrategiesExpanded: builder.query<{ results: MedScout.ChainedStrategy[] }, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const listResult = await fetchWithBQ(
          'v1/strategies/strategy-chains/'
        );
        if (listResult.error) return { error: listResult.error };

        const chains = (listResult.data as any)?.results || [];

        const detailedChains = await Promise.all(
          chains.map(async (chain: any) => {
            const detail = await fetchWithBQ(
              `v1/strategies/strategy-chains/${chain.id}/`
            );
            return detail.data || chain;
          })
        );

        return { data: { results: detailedChains } };
      },
      providesTags: [{ type: STRATEGY_TAG_TYPE, id: ALL_STRATEGIES_ID }],
    }),
    getChainedStrategyDetails: builder.query<MedScout.ChainedStrategy, string>({
      query: (id) => `v1/strategies/strategy-chains/${id}/`,
      providesTags: (result, error, id) => [{ type: STRATEGY_TAG_TYPE, id }],
    }),
    getMyChainedStrategies: builder.query<
      MedScout.PaginatedResponse & { results: MedScout.ChainedStrategy[] },
      MyChainedStrategiesParams | void
    >({
      query: (params) => {
        const queryParams = buildSearchParamsAuto(params || {});
        return {
          url: `v1/strategies/strategy-chains/my-chains/?${queryParams?.toString()}`,
        };
      },
      providesTags: (result) => {
        const strategies = result?.results || [];

        return [
          {
            type: STRATEGY_TAG_TYPE,
            id: MY_CHAINED_STRATEGIES_ID,
          },
          ...strategies.map(({ id }: any) => ({
            type: STRATEGY_TAG_TYPE,
            id,
          })),
        ];
      },
    }),
    updateChainedStrategy: builder.mutation<MedScout.ChainedStrategy, Record<string, any> & { id: number }>({
      query: (chainedStrategy) => ({
        url: `v1/strategies/strategy-chains/${chainedStrategy.id}/`,
        method: 'PATCH',
        body: chainedStrategy,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: STRATEGY_TAG_TYPE, id },
        {
          type: STRATEGY_TAG_TYPE,
          id: ALL_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: USER_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: MY_CHAINED_STRATEGIES_ID,
        },
      ],
    }),
    deleteChainedStrategy: builder.mutation<void, string>({
      query: (id) => ({
        url: `v1/strategies/strategy-chains/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: STRATEGY_TAG_TYPE, id },
        {
          type: STRATEGY_TAG_TYPE,
          id: ALL_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: USER_STRATEGIES_ID,
        },
        {
          type: STRATEGY_TAG_TYPE,
          id: MY_CHAINED_STRATEGIES_ID,
        },
      ],
    }),
  }),
});

export const {
  useGetAllChainedStrategiesQuery,
  useGetAllChainedStrategiesExpandedQuery,
  useGetProviderStrategyTabsQuery,
  useCreateChainedStrategyMutation,
  useGetChainedStrategyDetailsQuery,
  useUpdateChainedStrategyMutation,
  useDeleteChainedStrategyMutation,
  useGetMyChainedStrategiesQuery,
} = chainedStrategiesApi;
